import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { backendUrl } from './gameplanUtils';
import { useFiles, FileRow, RowBtn, ListHeader, TriageButtons, GroupBlock, SearchBox, filterFiles, Spinner, fmtD, daysTo, money } from './shared/FileList';

export const SettlementsView = ({ authToken, readOnly = false, onOpenFile, refreshSignal, onChanged }) => {
  const [files, reload] = useFiles(authToken, refreshSignal);
  const [q, setQ] = useState('');
  const [showStale, setShowStale] = useState(false);
  const done = () => { reload(); onChanged?.(); };

  const rows = useMemo(() => filterFiles((files || []).filter((f) => f.status_stage === 'approved'), q)
    .map((f) => ({ ...f, days: daysTo(f.settlement_date || f.projected_settlement_date) }))
    .sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999)), [files, q]);
  const live = rows.filter((f) => f.file.health !== 'STALE');
  const stale = rows.filter((f) => f.file.health === 'STALE');

  const markSettled = async (f) => {
    const r = await fetch(`${backendUrl}/api/gameplan/deals/${f.id}`, { method: 'PATCH', headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status_stage: 'settled', settled_date: new Date().toISOString().slice(0, 10) }) });
    if (r.ok) { toast.success(`${f.client_name} settled`); done(); } else toast.error('Could not mark settled');
  };
  if (!files) return <Spinner />;

  const dateCell = (f) => (f.settlement_date || f.projected_settlement_date)
    ? <>{fmtD(f.settlement_date || f.projected_settlement_date)}<span className={cn('ml-1.5 font-bold tabular-nums', f.days < 0 ? 'text-rose-600' : f.days <= 7 ? 'text-amber-700' : 'text-slate-500')}>{f.days < 0 ? `${-f.days}d overdue` : f.days === 0 ? 'today' : `in ${f.days}d`}</span></>
    : <span className="text-slate-400">No date</span>;

  return (
    <div className="space-y-4" data-testid="settlements-view">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div><h2 className="text-lg font-extrabold text-slate-900">Settlements</h2><p className="text-xs text-slate-500">Approved → settled · {live.length} files · {money(live.reduce((s, f) => s + Number(f.approved_amount || f.est_loan_amount || 0), 0))}</p></div>
        <SearchBox q={q} setQ={setQ} testid="settlements-search" />
      </div>
      <GroupBlock title="Heading to settlement" sub="soonest first" count={live.length} value={live.reduce((s, f) => s + Number(f.approved_amount || f.est_loan_amount || 0), 0)} testid="group-settlement">
        <ListHeader dateLabel="Settlement" />
        {live.length === 0 && <div className="px-6 py-10 text-center text-sm text-slate-500">No approved files awaiting settlement.</div>}
        {live.map((f) => <FileRow key={f.id} f={f} onOpen={onOpenFile} dateLabel="" dateValue={dateCell(f)} testid={`settlement-row-${f.id}`}
          actions={readOnly ? null : <RowBtn tone="lime" onClick={() => markSettled(f)} testid={`mark-settled-${f.id}`}>Mark settled</RowBtn>} />)}
      </GroupBlock>
      {stale.length > 0 && (
        <GroupBlock title="Needs triage" sub="approved before the workflow existed — did it settle?" count={stale.length} value={stale.reduce((s, f) => s + Number(f.approved_amount || f.est_loan_amount || 0), 0)} testid="group-stale" tone="muted">
          {!showStale ? <button onClick={() => setShowStale(true)} className="w-full py-3 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="show-stale">Show {stale.length} files to triage</button>
            : (<><ListHeader dateLabel="Approved" />{stale.map((f) => <FileRow key={f.id} f={f} onOpen={onOpenFile} dateLabel="" dateValue={fmtD(f.approved_date)} actions={readOnly ? null : <TriageButtons f={f} authToken={authToken} onDone={done} />} />)}</>)}
        </GroupBlock>
      )}
    </div>
  );
};

export default SettlementsView;
